/*
* Resideo/LifeWhere
* Copyright (C) 2018-2023 Resideo/LifeWhere
* mailto:nathan.williams@resideo.com
*/

import React, {useContext, useEffect, useState} from "react";
import "../../custom.css";
import {Tab, TabList, TabPanel, Tabs} from "react-tabs";
import {Spinner} from "react-bootstrap";
import {styled} from '@mui/system';
import {Event, PageView} from "../GoogleAnalytics";
import LongArrowDownIcon from "@mui/icons-material/ArrowDownward";
import LongArrowUpIcon from "@mui/icons-material/ArrowUpward";
import {FleetStateContext} from "./FleetStateProvider";
import {useNavigate} from "react-router-dom";
import Pagination from "@mui/material/Pagination";
import {AssetTypeDict} from "../../componentObjects";

const CUSTOMER = "CUSTOMER";
const TYPE = "TYPE";
const MAKE = "MAKE";
const MODEL = "MODEL";
const AGE = "AGE";
const ASC = "asc";
const DESC = "desc";
const SINGLE = "single";
const DUAL = "dual";
const VARIABLE = "variable";
const ECM = "ecm";
const PSC = "psc";
const SIC = "sic";
const SIN = "sin";
const SPARK = "spark";

const useStyles = styled((theme) => ({
    root: {
        [theme.breakpoints.down('md')]: {
            marginLeft: '-225px !important',
        },
    },
    loader: {
        marginLeft: '225px',
        [theme.breakpoints.down('md')]: {
            marginLeft: '-225px !important',
            width: `calc(100% + 450px)`,
        },
    }
}));

const csvBaseName = "Fleet.csv";

function convertToCSV(list) {
    
    let data = list.map(item => {
        return {
            customer: item.customer?.name,
            make: item.device?.make,
            model: item.device?.model,
            age: item.device?.age,
            type: AssetTypeDict?.find(d => d.id === item.device?.assetTypeId)?.name
        }
    });
    const array = [Object.keys(data[0])].concat(data.map(obj => Object.values(obj)));
    return array.map(row => row.join(',')).join('\n');
}

function downloadCSV(data) {
    Event("Download Data Clicked", "User clicked download data button in Fleet", "Download Data Clicked");
    let filenameWithDate = csvBaseName;
    const csvContent = "data:text/csv;charset=utf-8," + encodeURIComponent(convertToCSV(data));

    const link = document.createElement("a");
    link.setAttribute("href", csvContent);
    link.setAttribute("download", filenameWithDate || "data.csv");
    document.body.appendChild(link);
    link.click();
}

function Fleet({ isLoading, setActivePath, roles = [] }) {
    const classes = useStyles();
    const history = useNavigate();

    const { partnerFleetDataList, isLoadingData, isShowAll, setIsShowAll } = useContext(FleetStateContext);

    const rowsPerPage = 20;
    
    const [isMounted, setIsMounted] = useState(false);
    const [data, setData] = useState([]);
    const [columnSort, setColumnSort] = useState({ column: "", direction: ASC });
    const [selectedTab, setSelectedTab] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [upgradeOpportunities, setUpgradeOpportunities] = useState({
        singleECMCount: 0,
        singlePSCCount: 0,
        dualECMCount: 0,
        dualPSCCount: 0,
        variableECMCount: 0,
        variablePSCCount: 0,
        igniterSICCount: 0,
        igniterSINCount: 0,
        igniterSparkCount: 0
    });

    const totalPages = Math.ceil(data.length / rowsPerPage);
    const paginatedRows = data.slice((currentPage - 1) * rowsPerPage, currentPage * rowsPerPage);

    useEffect(() => {
        setIsMounted(true);
        PageView();
        
        return () => {
            setIsMounted(false);
        }
    }, []);

    useEffect(() => {
        if (isMounted && partnerFleetDataList) {
            setData(partnerFleetDataList);
            calculateUpgradeOpportunities(partnerFleetDataList);
        }
    }, [isMounted, partnerFleetDataList]);
    
    const calculateUpgradeOpportunities = (dataList) => {
        let counts = {
            singleECMCount: 0,
            singlePSCCount: 0,
            dualECMCount: 0,
            dualPSCCount: 0,
            variableECMCount: 0,
            variablePSCCount: 0,
            igniterSICCount: 0,
            igniterSINCount: 0,
            igniterSparkCount: 0
        };

        for (let i = 0; i < dataList.length; i++) {
            let item = dataList[i];

            if (item.device.propertiesJson) {
                let properties = JSON.parse(item.device.propertiesJson);

                if (properties.BlowerStages && properties.BlowerType) {
                    if (properties.BlowerStages.toLowerCase() === SINGLE && properties.BlowerType.toLowerCase() === ECM) {
                        counts.singleECMCount++;
                    } else if (properties.BlowerStages.toLowerCase() === SINGLE && properties.BlowerType.toLowerCase() === PSC) {
                        counts.singlePSCCount++;
                    } else if (properties.BlowerStages.toLowerCase() === DUAL && properties.BlowerType.toLowerCase() === ECM) {
                        counts.dualECMCount++;
                    } else if (properties.BlowerStages.toLowerCase() === DUAL && properties.BlowerType.toLowerCase() === PSC) {
                        counts.dualPSCCount++;
                    } else if (properties.BlowerStages.toLowerCase() === VARIABLE && properties.BlowerType.toLowerCase() === ECM) {
                        counts.variableECMCount++;
                    } else if (properties.BlowerStages.toLowerCase() === VARIABLE && properties.BlowerType.toLowerCase() === PSC) {
                        counts.variablePSCCount++;
                    }
                }
                
                if (properties.IgniterType) {
                    if (properties.IgniterType.toLowerCase() === SIC) {
                        counts.igniterSICCount++;
                    } else if (properties.IgniterType.toLowerCase() === SIN) {
                        counts.igniterSINCount++;
                    } else if (properties.IgniterType.toLowerCase() === SPARK) {
                        counts.igniterSparkCount++;
                    }    
                }
            }
        }

        setUpgradeOpportunities(counts);
    }

    const iconFunction = (columnName)  => {
        let arrowIcon = columnSort.direction === ASC ?
                <LongArrowDownIcon className={"arrow-icon"}/>
                : <LongArrowUpIcon className={"arrow-icon"} />;

        return columnSort.column === columnName ?
            <span className={"height-15 pad-left-2"}>
                {arrowIcon}
            </span>
            : null;
    }

    const onColumnClick = (column) => {
        const newDirection = columnSort.direction === ASC ? DESC : ASC;
        setColumnSort({ column, direction: newDirection });
        sortTable(column);
    };

    const sortTable = (column) => {
        let sortedList = [...data];
        
        if (column === MAKE || column === MODEL || column === CUSTOMER) {
            sortedList.sort((a, b) => {
                const aValue = column === CUSTOMER ? a.customer?.name : a.device[column.toLowerCase()];
                const bValue = column === CUSTOMER ? b.customer?.name : b.device[column.toLowerCase()];

                if (aValue < bValue) {
                    return columnSort.direction === ASC ? -1 : 1;
                }

                if (aValue > bValue) {
                    return columnSort.direction === ASC ? 1 : -1;
                }

                return 0;
            });
        } else if (column === AGE) {
            sortedList.sort((a, b) =>
                columnSort.direction === ASC ? a.device.age - b.device.age : b.device.age - a.device.age
            );
        }
        else if (column === TYPE) {
            sortedList.sort((a, b) => {
                const aValue = AssetTypeDict?.find(d => d.id === a.device?.assetTypeId)?.name;
                const bValue = AssetTypeDict?.find(d => d.id === b.device?.assetTypeId)?.name;

                if (aValue < bValue) {
                    return columnSort.direction === ASC ? -1 : 1;
                }

                if (aValue > bValue) {
                    return columnSort.direction === ASC ? 1 : -1;
                }

                return 0;
            });
        }

        setData(sortedList);
    };

    const onBlowerTypesClick = () => {
        Event("Blower Types table clicked", "User clicked blower types table to go to upgrade opportunities page", "Blower Types table clicked");
        setActivePath("/upgradeOpportunities");

        const location = {
            pathname: "/upgradeOpportunities",
            state: {
                currentTab: 'blowerTypes'
            }
        }

        history(location);
    }

    const onIgniterTypesClick = () => {
        Event("Igniter Types table clicked", "User clicked ignitier types table to go to upgrade opportunities page", "Igniter Types table clicked");
        setActivePath("/upgradeOpportunities");

        const location = {
            pathname: "/upgradeOpportunities",
            state: {
                currentTab: 'igniterTypes'
            }
        }

        history(location);
    }
    
    const handleDownloadData = () => {
        downloadCSV(data);
    }
    
    const renderAgeOfFleetContent = () => {
        return isLoadingData ?
            <div className={["loading", "display-block", classes.root].join(' ')}>
                <div className="loading-wrapper">
                    <div className="modal-body"><Spinner animation="border" variant="light" /></div>
                </div></div>
            : renderAgeOfFleetTable();
    }
    
    const renderAgeOfFleetTable = () => {
        let tableContent = <tbody>
        {paginatedRows.map((item, index) => (
            <tr className={"alerts-table-row text-center"} key={index}>
                <td className={"border-left"}>{item.customer.name}</td>
                <td>{item.device.make}</td>
                <td>{item.device.model}</td>
                <td>{item.device.age}</td>
                <td className={"border-right"}>{AssetTypeDict?.find(d => d.id === item.device.assetTypeId)?.name}</td>
            </tr>
        ))}
        </tbody>;

        let pagination =
            <div className={"display-flex justify-content-center"}>
                <Pagination count={totalPages} page={currentPage} onChange={onChangePage} showFirstButton={totalPages > 7} showLastButton={totalPages > 7} size="small" />
            </div>;

        return (
            <div className={"margin-20-30"}>
                <div>
                    <table className="table-striped table-hover width-100p table-layout-fixed">
                        <thead>
                            <tr className="alerts-table-row text-center">
                                <th>
                                    <div onClick={() => onColumnClick(CUSTOMER)}>
                                        <span className={"cursor-pointer"}>CUSTOMER</span>
                                        {iconFunction(CUSTOMER)}
                                    </div>
                                </th>
                                <th>
                                    <div onClick={() => onColumnClick(MAKE)}>
                                        <span className={"cursor-pointer"}>MAKE</span>
                                        {iconFunction(MAKE)}
                                    </div>
                                </th>
                                <th>
                                    <div onClick={() => onColumnClick(MODEL)}>
                                        <span className={"cursor-pointer"}>MODEL</span>
                                        {iconFunction(MODEL)}
                                    </div>
                                </th>
                                <th>
                                    <div onClick={() => onColumnClick(AGE)}>
                                        <span className={"cursor-pointer"}>AGE</span>
                                        {iconFunction(AGE)}
                                    </div>
                                </th>
                                <th>
                                    <div onClick={() => onColumnClick(TYPE)}>
                                        <span className={"cursor-pointer"}>TYPE</span>
                                        {iconFunction(TYPE)}
                                    </div>
                                </th>
                            </tr>
                        </thead>
                        {tableContent}
                    </table>
                    {pagination}
                </div>
            </div>
        );
    }

    const renderUpgradeOpportunitiesContent = () => {
        return isLoadingData ?
            <div className={["loading", "display-block", classes.root].join(' ')}>
                <div className="loading-wrapper">
                    <div className="modal-body"><Spinner animation="border" variant="light" /></div>
                </div></div>
            : renderUpgradeOpportunitiesTables();
    }

    const renderUpgradeOpportunitiesTables = () => {
        let blowerTypeTableContent = <tbody>
            <tr className={"alerts-table-row text-center"}>
                <td className={"border-left"}>{'Single'}</td>
                <td>{upgradeOpportunities.singleECMCount}</td>
                <td className={"border-right"}>{upgradeOpportunities.singlePSCCount}</td>
            </tr>
            <tr className={"alerts-table-row text-center"}>
                <td className={"border-left"}>{'Dual'}</td>
                <td>{upgradeOpportunities.dualECMCount}</td>
                <td className={"border-right"}>{upgradeOpportunities.dualPSCCount}</td>
            </tr>
            <tr className={"alerts-table-row text-center"}>
                <td className={"border-left"}>{'Variable'}</td>
                <td>{upgradeOpportunities.variableECMCount}</td>
                <td className={"border-right"}>{upgradeOpportunities.variablePSCCount}</td>
            </tr>
        </tbody>;

        let igniterTypeTableContent = <tbody>
            <tr className={"alerts-table-row text-center"} >
                <td className={"border-left"}>{'SIC'}</td>
                <td className={"border-right"}>{upgradeOpportunities.igniterSICCount}</td>
            </tr>
            <tr className={"alerts-table-row text-center"} >
                <td className={"border-left"}>{'SIN'}</td>
                <td className={"border-right"}>{upgradeOpportunities.igniterSINCount}</td>
            </tr>
            <tr className={"alerts-table-row text-center"} >
                <td className={"border-left"}>{'Spark'}</td>
                <td className={"border-right"}>{upgradeOpportunities.igniterSparkCount}</td>
            </tr>
        </tbody>;

        return (
            <div className={"margin-20-30"}>
                <div>
                    <div className={"display-flex align-items-center justify-content-space-between"}>
                        <h6 className={"margin-bottom-0"}>Blower Types</h6>
                        <button type="button" className="secondary-btn btn-small" onClick={onBlowerTypesClick}>
                            View All
                        </button>
                    </div>
                    <table className="table-striped table-hover width-100p table-layout-fixed">
                        <thead>
                        <tr className="alerts-table-row text-center">
                            <th>
                                <div>
                                    <span className={"cursor-pointer"}>BLOWER</span>
                                </div>
                            </th>
                            <th>
                                <div>
                                    <span className={"cursor-pointer"}>ECM</span>
                                </div>
                            </th>
                            <th>
                                <div>
                                    <span className={"cursor-pointer"}>PSC</span>
                                </div>
                            </th>
                        </tr>
                        </thead>
                        {blowerTypeTableContent}
                    </table>

                    <div className={"width-100p"}>
                        <div className={"width-50p display-flex align-items-center margin-top-50 justify-content-space-between"}>
                            <h6 className={"margin-bottom-0"}>Igniter Types</h6>
                            <button type="button" className="secondary-btn btn-small" onClick={onIgniterTypesClick}>
                                View All
                            </button>
                        </div>
                        <table className="table-striped table-hover width-50p table-layout-fixed">
                            <thead>
                            <tr className="alerts-table-row text-center">
                                <th>
                                    <div>
                                        <span className={"cursor-pointer"}>IGNITER</span>
                                    </div>
                                </th>
                                <th>
                                    <div>
                                        <span className={"cursor-pointer"}>COUNT</span>
                                    </div>
                                </th>
                            </tr>
                            </thead>
                            {igniterTypeTableContent}
                        </table>
                    </div>

                </div>
            </div>
        );
    }
    
    const renderShowAllButton = () => {
        return (
            roles.includes("ADMIN") ?
                    <span>
                        {isShowAll ? <p className={"margin-right-10"}>*Showing all assets in system</p> : null}
                        <button className={isShowAll ? "primary-btn btn-large width-150" : "secondary-btn btn-large width-150"} onClick={onShowAllClicked}>Show All</button>
                    </span> : null
        );
    }
    
    const renderDownloadDataButton = () => {
        return (
                <span>
                    {isShowAll ? <p className={"margin-right-10 color-transparent"}>placeholder</p> : null}
                    <button className={"secondary-btn btn-large width-150"} onClick={handleDownloadData}>Download Data</button>
                </span>
        )
    }
    
    const onShowAllClicked = () => {
        setCurrentPage(1);
        setIsShowAll(!isShowAll);
    }

    const onChangePage = (e, page) => {
        setCurrentPage(page);
    };

    return (
        <div>
            <div>
                <div className='infoPage'>
                    <h2 className="pageTitle dashboard-title" id="tabelLabel">
                        Partner Fleet
                    </h2>

                    <div className="row no-gutters alerts-tab-wrapper margin-0-20 pad-10-0">
                        <span className="btn-right pad-25-25-25-30">
                            {renderShowAllButton()}
                            {renderDownloadDataButton()}
                        </span>
                    </div>
                    
                    
                    <Tabs className={"margin-top-20 pad-left-15"} selectedIndex={selectedTab} onSelect={(index) => setSelectedTab(index)}>
                        <TabList>
                            <Tab>Age of Fleet</Tab>
                            <Tab>Upgrade Opportunities</Tab>
                        </TabList>

                        <TabPanel>
                            {renderAgeOfFleetContent()}
                        </TabPanel>

                        <TabPanel>
                            {renderUpgradeOpportunitiesContent()}
                        </TabPanel>
                    </Tabs>
                </div>
            </div>
        </div>
    );
}
export default Fleet;
